/**
 * AuthZEN Initializer Component
 * 
 * Wraps your app to initialize the AuthZEN system with:
 * - PDP metadata discovery
 * - Permission preloading
 * - Cache restoration
 * - Smart loading states
 * 
 * Use this at the root of your app for instant permission checks throughout.
 */

import React, { useEffect, useState } from 'react';
import { PDPDiscovery } from './authz-discovery';
import { AuthZENCache } from './authz-cache';
import { AuthZENPreloader, type PermissionSpec, type PreloadResult } from './authz-preload';
import { getValidatedSubject } from './authz-subject';

export interface AuthZENInitOptions {
	/**
	 * Preload permissions based on user role
	 * Default: true
	 */
	preloadByRole?: boolean;
	
	/**
	 * Custom permissions to preload (overrides role-based)
	 */
	customPreload?: PermissionSpec[];
	
	/**
	 * Show loading screen while initializing
	 * Default: true
	 */
	showLoading?: boolean;
	
	/**
	 * Custom loading component
	 */
	loadingComponent?: React.ReactNode;
	
	/**
	 * Minimum display time for loading screen (ms)
	 * Prevents flash of loading screen
	 * Default: 0
	 */
	minLoadingTime?: number;
	
	/**
	 * Enable debug logging
	 */
	debug?: boolean;
	
	/**
	 * Callback when initialization complete
	 */
	onReady?: (result: PreloadResult) => void;
	
	/**
	 * Callback when initialization fails
	 */
	onError?: (error: Error) => void;
}

/**
 * AuthZEN System Initializer
 * 
 * Wrap your app with this component to initialize the AuthZEN system.
 * Handles PDP discovery and permission preloading for instant UI rendering.
 * 
 * @example
 * ```tsx
 * // Basic usage (auto-preload based on user role)
 * <AuthZENInitializer>
 *   <App />
 * </AuthZENInitializer>
 * 
 * // Custom preload
 * <AuthZENInitializer
 *   customPreload={[
 *     { resource: 'dashboard', actions: ['view', 'customize'] },
 *     { resource: 'reports', actions: ['view', 'export'] }
 *   ]}
 *   onReady={(result) => console.log('Preloaded', result.preloadedCount, 'permissions')}
 * >
 *   <App />
 * </AuthZENInitializer>
 * 
 * // Custom loading screen
 * <AuthZENInitializer
 *   loadingComponent={<BrandedSplashScreen />}
 *   minLoadingTime={500}
 * >
 *   <App />
 * </AuthZENInitializer>
 * ```
 */
export function AuthZENInitializer({
	children,
	preloadByRole = true,
	customPreload,
	showLoading = true,
	loadingComponent,
	minLoadingTime = 0,
	debug = false,
	onReady,
	onError
}: AuthZENInitOptions & { children: React.ReactNode }) {
	const [ready, setReady] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const [initResult, setInitResult] = useState<PreloadResult | null>(null);
	
	useEffect(() => {
		let mounted = true;
		const initStartTime = performance.now();
		
		(async () => {
			try {
				if (debug) {
					console.group('[AuthZ Init] Starting AuthZEN initialization');
				}
				
				// Step 1: Discover PDP metadata
				if (debug) console.info('[AuthZ Init] Step 1: Discovering PDP metadata...');
				
				const metadata = await PDPDiscovery.discover();
				
				if (debug) {
					console.info('[AuthZ Init] ✅ PDP discovered:', {
						pdp: metadata.policy_decision_point,
						name: metadata.pdp_name,
						version: metadata.authzen_version,
						capabilities: metadata.capabilities?.length || 0
					});
				}
				
				// Step 2: Restore cache from localStorage
				if (debug) console.info('[AuthZ Init] Step 2: Restoring cache from localStorage...');
				
				const restoredCount = await AuthZENCache.preload();
				
				if (debug && restoredCount > 0) {
					console.info(`[AuthZ Init] ✅ Restored ${restoredCount} cached decisions`);
				}
				
				// Step 3: Get current user
				if (debug) console.info('[AuthZ Init] Step 3: Getting current user...');
				
				const subject = await getValidatedSubject();
				
				if (debug) {
					console.info('[AuthZ Init] ✅ Subject validated:', {
						type: subject.type,
						id: subject.id
					});
				}
				
				// Step 4: Preload permissions
				let result: PreloadResult;
				
				if (customPreload) {
					if (debug) console.info('[AuthZ Init] Step 4: Preloading custom permissions...');
					result = await AuthZENPreloader.preloadCommonPermissions(customPreload, { debug });
				} else if (preloadByRole) {
					if (debug) console.info('[AuthZ Init] Step 4: Preloading role-based permissions...');
					
					// Get user with role info
					const userWithRole = {
						id: subject.id,
						role: (subject.properties as any)?.role,
						roles: (subject.properties as any)?.roles
					};
					
					result = await AuthZENPreloader.preloadForRole(userWithRole);
				} else {
					// Warmup cache with critical permissions only
					if (debug) console.info('[AuthZ Init] Step 4: Warming up cache...');
					result = await AuthZENPreloader.warmupCache();
				}
				
				const totalInitTime = performance.now() - initStartTime;
				
				if (debug) {
					console.info('[AuthZ Init] ✅ Initialization complete', {
						totalTime: `${totalInitTime.toFixed(0)}ms`,
						preloaded: result.preloadedCount,
						cacheSize: AuthZENCache.getStats().size
					});
					console.groupEnd();
				}
				
				// Enforce minimum loading time to prevent flash
				if (minLoadingTime > 0) {
					const elapsed = performance.now() - initStartTime;
					const remaining = minLoadingTime - elapsed;
					
					if (remaining > 0) {
						await new Promise(resolve => setTimeout(resolve, remaining));
					}
				}
				
				if (mounted) {
					setInitResult(result);
					setReady(true);
					setError(null);
					
					if (onReady) {
						onReady(result);
					}
				}
				
			} catch (err) {
				const error = err as Error;
				
				console.error('[AuthZ Init] Initialization failed:', error);
				
				if (debug) {
					console.groupEnd();
				}
				
				if (mounted) {
					setError(error);
					setReady(true);  // Continue anyway (permissions load on-demand)
					
					if (onError) {
						onError(error);
					}
				}
			}
		})();
		
		return () => {
			mounted = false;
		};
	}, [preloadByRole, customPreload, minLoadingTime, debug, onReady, onError]);
	
	// Show loading screen if not ready
	if (!ready && showLoading) {
		if (loadingComponent) {
			return <>{loadingComponent}</>;
		}
		
		return <DefaultLoadingScreen />;
	}
	
	// Show error screen if initialization failed
	if (error) {
		return (
			<DefaultErrorScreen 
				error={error}
				onRetry={() => window.location.reload()}
			/>
		);
	}
	
	// Ready - render app
	return <>{children}</>;
}

/**
 * Default loading screen (professional branded)
 */
function DefaultLoadingScreen() {
	return (
		<div style={{
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			height: '100vh',
			background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
			color: 'white'
		}}>
			<div style={{
				width: 60,
				height: 60,
				border: '4px solid rgba(255,255,255,0.3)',
				borderTop: '4px solid white',
				borderRadius: '50%',
				animation: 'spin 1s linear infinite'
			}} />
			
			<div style={{
				marginTop: 24,
				fontSize: 18,
				fontWeight: 600
			}}>
				Initializing Authorization...
			</div>
			
			<div style={{
				marginTop: 8,
				fontSize: 14,
				opacity: 0.9
			}}>
				Discovering capabilities and preloading permissions
			</div>
			
			<style>{`
				@keyframes spin {
					0% { transform: rotate(0deg); }
					100% { transform: rotate(360deg); }
				}
			`}</style>
		</div>
	);
}

/**
 * Default error screen
 */
function DefaultErrorScreen({ 
	error, 
	onRetry 
}: { 
	error: Error; 
	onRetry: () => void;
}) {
	return (
		<div style={{
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			height: '100vh',
			padding: 24
		}}>
			<div style={{
				fontSize: 48,
				marginBottom: 16
			}}>
				⚠️
			</div>
			
			<h2 style={{
				fontSize: 24,
				fontWeight: 600,
				marginBottom: 12,
				color: '#1f2937'
			}}>
				Authorization Initialization Failed
			</h2>
			
			<p style={{
				fontSize: 14,
				color: '#6b7280',
				marginBottom: 24,
				maxWidth: 400,
				textAlign: 'center'
			}}>
				{error.message}
			</p>
			
			<button
				onClick={onRetry}
				style={{
					padding: '12px 24px',
					background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
					color: 'white',
					border: 'none',
					borderRadius: 8,
					fontSize: 14,
					fontWeight: 600,
					cursor: 'pointer'
				}}
			>
				Retry
			</button>
			
			<div style={{
				marginTop: 24,
				fontSize: 12,
				color: '#9ca3af'
			}}>
				The app will continue in degraded mode.
				Permissions will be checked on-demand.
			</div>
		</div>
	);
}

/**
 * Minimal initializer (no loading screen)
 * 
 * For cases where you want to initialize in background
 * without blocking app render
 * 
 * @example
 * ```tsx
 * function App() {
 *   useAuthZENBackgroundInit();
 *   
 *   return <YourApp />;  // ✅ Renders immediately
 *   // Permissions preload in background
 * }
 * ```
 */
export function useAuthZENBackgroundInit(
	permissions?: PermissionSpec[],
	debug: boolean = false
) {
	const [status, setStatus] = useState<'idle' | 'initializing' | 'ready' | 'error'>('idle');
	
	useEffect(() => {
		let mounted = true;
		
		setStatus('initializing');
		
		(async () => {
			try {
				// Discover PDP
				await PDPDiscovery.discover();
				
				// Restore cache
				await AuthZENCache.preload();
				
				// Preload permissions
				if (permissions) {
					await AuthZENPreloader.preloadCommonPermissions(permissions, { debug });
				} else {
					const subject = await getValidatedSubject();
					const user = {
						id: subject.id,
						role: (subject.properties as any)?.role,
						roles: (subject.properties as any)?.roles
					};
					await AuthZENPreloader.preloadForRole(user);
				}
				
				if (mounted) {
					setStatus('ready');
				}
				
			} catch (error) {
				console.error('[AuthZ Init] Background init failed:', error);
				
				if (mounted) {
					setStatus('error');
				}
			}
		})();
		
		return () => {
			mounted = false;
		};
	}, [permissions, debug]);
	
	return status;
}

